/**
 * Core type definitions for OneRealm
 */

export type Entity = number;

export type FactionId = 'yan' | 'han' | 'lin' | 'shang' | 'xuan' | 'tie' | 'yun' | 'neutral';

export type UnitType = 'sword' | 'archer' | 'spear' | 'cavalry' | 'shield' | 'taoist';

export type BuildingType = 'capital' | 'barracks' | 'tower' | 'mine' | 'farm' | 'market' | 'stable' | 'smithy' | 'temple' | 'archery' | 'spearcamp' | 'wall';

// 8种地形类型：平原 + 7种国家特色地形
export type TerrainType =
  | 'plain'     // 平原 - 中性地形
  | 'water'     // 水域
  | 'volcano'   // 火山/熔岩 - 炎国特色
  | 'snow'      // 雪原 - 寒国特色
  | 'forest'    // 森林 - 林国特色
  | 'grassland' // 草原 - 商国特色
  | 'swamp'     // 沼泽 - 玄国特色
  | 'highland'  // 山丘 - 铁国特色
  | 'desert';   // 荒漠 - 云国特色

export interface TerrainEffect {
  speedModifier: number;
  attackModifier: number;
  defenseModifier: number;
  factionBonus?: FactionId; // If set, this faction ignores negative terrain effects
}

// 每个国家对应的特色地形
export const FACTION_TERRAIN: Record<FactionId, TerrainType> = {
  yan: 'volcano',    // 炎国 - 火山
  han: 'snow',       // 寒国 - 雪原
  lin: 'forest',     // 林国 - 森林
  shang: 'desert',   // 商国 - 荒漠
  xuan: 'swamp',     // 玄国 - 沼泽
  tie: 'highland',   // 铁国 - 山丘
  yun: 'grassland',  // 云国 - 草原
  neutral: 'plain',  // 中立 - 平原
};

export const TERRAIN_EFFECTS: Record<TerrainType, TerrainEffect> = {
  plain: {
    speedModifier: 1.0,
    attackModifier: 1.0,
    defenseModifier: 1.0,
  },
  water: {
    speedModifier: 0.5,
    attackModifier: 1.0,
    defenseModifier: 0.8,
  },
  volcano: {
    speedModifier: 0.7,
    attackModifier: 1.2,
    defenseModifier: 0.9,
    factionBonus: 'yan', // 炎国无视火山惩罚
  },
  snow: {
    speedModifier: 0.6,
    attackModifier: 1.0,
    defenseModifier: 1.1,
    factionBonus: 'han', // 寒国无视雪原惩罚
  },
  forest: {
    speedModifier: 0.7,
    attackModifier: 1.0,
    defenseModifier: 1.15,
    factionBonus: 'lin', // 林国无视森林惩罚
  },
  grassland: {
    speedModifier: 1.1,
    attackModifier: 1.0,
    defenseModifier: 0.95,
    factionBonus: 'shang', // 商国草原加成
  },
  swamp: {
    speedModifier: 0.5,
    attackModifier: 0.9,
    defenseModifier: 1.1,
    factionBonus: 'xuan', // 玄国无视沼泽惩罚
  },
  highland: {
    speedModifier: 0.8,
    attackModifier: 1.0,
    defenseModifier: 1.25,
    factionBonus: 'tie', // 铁国山丘加成
  },
  desert: {
    speedModifier: 0.85,
    attackModifier: 1.0,
    defenseModifier: 1.0,
    factionBonus: 'yun', // 云国荒漠加成
  },
};

export interface FactionData {
  id: FactionId;
  name: string;
  color: number;
  secondaryColor: number;
  attackBonus: number;
  defenseBonus: number;
  speedBonus: number;
  incomeBonus: number;
}

export interface UnitStats {
  type: UnitType;
  health: number;
  attack: number;
  defense: number;
  attackSpeed: number;
  moveSpeed: number;
  range: number;
  cost: number;
  trainTime: number;
}

export interface BuildingSize {
  width: number;  // X axis tiles
  depth: number;  // Z axis tiles
}

export interface BuildingStats {
  type: BuildingType;
  health: number;
  cost: number;
  buildTime: number;
  size: BuildingSize;
  income?: number;
  spawnType?: UnitType;
  spawnInterval?: number;
}

export interface GameConfig {
  chunkSize: number;
  tileSize: number;
  viewDistance: number;
  territoryCell: number;
  maxUnits: number;
  startingGold: number;
  baseIncome: number;
  territoryIncome: number;
  mapSize: number;
}

export const DEFAULT_CONFIG: GameConfig = {
  chunkSize: 16,
  tileSize: 1.0,
  viewDistance: 12,
  territoryCell: 28,
  maxUnits: 2000,
  startingGold: 500,
  baseIncome: 100,
  territoryIncome: 5,
  mapSize: 256,
};
